import type { ReactNode } from 'react';

import { colors } from '@packages/core/styles';
import { Typography } from '@packages/ui/shared';
import { createUseStyles } from 'react-jss';

import { ssm5TextStyles } from '../ssm5.styles';

const useStyles = createUseStyles({
    box: {
        alignItems: 'center',
        backgroundColor: colors.grayOne,
        borderLeft: `4px solid ${colors.blueFour}`,
        borderRadius: 4,
        display: 'flex',
        gap: 16,
        padding: 16,
    },
    headingRow: {
        alignItems: 'center',
        display: 'flex',
        gap: 8,
    },
    lines: {
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        gap: 8,
        minWidth: 0,
    },
});

/** Props for {@link Ssm5NotificationBox}. */
export type Ssm5NotificationBoxProps = {
    /** Rendered on the right side of the box. */
    action?: ReactNode;
    /** Rendered next to the heading. */
    badge?: ReactNode;
    /** The second line. */
    body: string;
    /** The first line. */
    heading: string;
};

/**
 * The gray notification box of the design system: a left-accented banner with a heading line, a
 * body line and a right-side action.
 *
 * @param props - {@link Ssm5NotificationBoxProps}
 * @returns the box
 */
export function Ssm5NotificationBox({ action, badge, body, heading }: Ssm5NotificationBoxProps) {
    const classes = useStyles();

    return (
        <section className={classes.box}>
            <div className={classes.lines}>
                <div className={classes.headingRow}>
                    <Typography color="blueOne" style={ssm5TextStyles.bodySemibold} variant="default">
                        {heading}
                    </Typography>
                    {badge}
                </div>
                <Typography color="textDarkSecondary" style={ssm5TextStyles.body} variant="default">
                    {body}
                </Typography>
            </div>
            {action}
        </section>
    );
}
